/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import Head from "next/head";
import Link from "next/link";
import { Component } from "react";
import LazyLoad from "react-lazy-load";

import Banner from "../../components/banner";
import Newsletter from "../../components/newsletter";
import OpenGraphHead from "../../components/og-head";
import TwitterHead from "../../components/twitter-head";
import {
  toAbsolutePageURL,
  toRelativePageURL,
} from "../../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class ChildrensDefenseFund extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render() {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/partners/cdf-childrens-defense-fund", market);
    const alt = "Lasting Smiles";
    const title = "Children's Defense Fund · Partners · Lasting Smiles";
    const description = "Give back to Children's Defense Fund with Lasting Smiles organic and all natural lip balm. " +
      "Smile for a cause. " +
      "Give back to charitable causes you care about with sustainable, ethical, and organic skin care products that make a difference all year long. " +
      "The #1 socially responsible and ethical lip care and do good lip balm choice to support your favorite social and environmental non-profit causes caring for people and our planet. " +
      "The perfect gift that gives back.";

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/partners/cdf-childrens-defense-fund", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/partners/cdf-childrens-defense-fund", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/purpose", market),
                    "name": "Causes",
                    "position": 2,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/partners/cdf-childrens-defense-fund", market),
                    "name": "Children's Defense Fund",
                    "position": 3,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <Banner />
        <Newsletter />
        <section
          className="bg-yellow-100 px-2 py-10 sm:px-5"
          style={{
            backgroundImage: `url("${require("../../public/images/backgrounds/purpose.jpg")}")` as string,
            backgroundPositionY: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="max-w-screen-lg mx-auto">
            <h2
              className="font-extrabold font-serif sm:text-6xl text-5xl text-center uppercase"
              style={{
                background: "linear-gradient(to bottom right, #FAC551, #F36F48, #F3B411, #F7A4B6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Smile for a&nbsp;Cause
            </h2>
            <picture>
              <source
                srcSet={require("../../public/images/logos/cdf-wide.webp") as string}
                type="image/webp"
              />
              <source
                srcSet={require("../../public/images/logos/cdf-wide.png") as string}
                type="image/png"
              />
              <img
                alt="Children's Defense Fund"
                className="block mx-auto"
                src={require("../../public/images/logos/cdf-wide.png") as string}
                style={{ maxHeight: 200 }}
              />
            </picture>
          </div>
        </section>
        <section className="py-10">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif px-2 sm:px-5 text-3xl text-center">
              Children's Defense Fund
            </h1>
            <p className="px-2 sm:px-5 text-xl text-center">
              The Children's Defense Fund Leave No Child Behind&reg; mission is to ensure every child has a Healthy Start, a Head Start, a Fair Start, a Safe Start and a Moral Start in life and a successful passage to adulthood with the help of caring families and communities.
            </p>
            <div className="overflow-x-auto overflow-y-hidden scrolling-touch sm:max-w-screen-md sm:mx-auto sm:px-5">
              <div className="flex-row inline-flex sm:flex sm:flex-wrap sm:items-stretch sm:justify-around text-white">
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/cdf/photo-1.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/cdf/photo-1.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Children's Defense Fund Photo 1"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/cdf/photo-1.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/cdf/photo-2.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/cdf/photo-2.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Children's Defense Fund Photo 2"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/cdf/photo-2.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/cdf/photo-3.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/cdf/photo-3.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Children's Defense Fund Photo 3"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/cdf/photo-3.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/cdf/photo-4.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/cdf/photo-4.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Children's Defense Fund Photo 4"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/cdf/photo-4.jpg") as string}
                    />
                  </picture>
                </div>
              </div>
            </div>
            <p className="px-2 sm:px-5 text-xl text-center">
              Your support of Children's Defense Fund provides a strong, effective and independent voice for all the children of America who cannot vote, lobby or speak for themselves.
              We pay particular attention to the needs of poor children, children of color and those with disabilities.
              CDF educates the nation about the needs of children and encourages preventive investments before they get sick, drop out of school, get into trouble or suffer family breakdown.
            </p>
          </div>
        </section>
        <section className="bg-gray-100 px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              David Williams' story
            </h2>
            <p className="text-center">
              &ldquo;I started with CDF Freedom Schools at about age 5.
              I got more out of CDF Freedom Schools than the regular public school system.
              It started to kick in that literacy was important and the love of reading became a passion for me.
              Reading about my culture and my people... you learn things you don't read in regular history books.
              Every summer I made sure to keep coming back.
              <br />
              After a few years in CDF Freedom Schools, when I was just 8 years old, I promised myself that I would make a difference when I got older by being a role model for children in my hometown of Bennettsville, South Carolina.
              And that's exactly what I am doing.
              <br />
              I'm currently a school teacher, influenced by my time as a young student in CDF Freedom Schools.
              I work to be a positive role model, especially for the young men.
              That's something around this community that we really don't have enough of.
              I want to instill in them that they can and they will achieve their dreams.
              We want our kids to reach greatness, to prove people wrong, and overcome stereotypes.
              <br />
              It's hard to overstate the power of CDF Freedom Schools.
              Outside the classroom, you don't know what is going on in your scholars' lives: hunger, neglect, or trauma.
              But once they enter your caring environment, all of that goes away.
              Once they're in that classroom, it's a family.&rdquo;
            </p>
            <div className="text-center">
              <Link
                as={toRelativePageURL("/products", market as string)}
                href={{
                  pathname: "/products",
                  query: { market },
                }}
              >
                <a className="bg-black border border-black duration-500 ease-in-out font-medium font-serif hover:bg-default hover:text-black inline-block my-5 px-5 py-3 rounded shadow uppercase text-lg text-white transition">
                  Shop &amp; Make a Difference
                </a>
              </Link>
            </div>
          </div>
        </section>
        <section className="bg-water px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              Watch how Children's Defense Fund creates lasting smiles by Leaving No&nbsp;Child&nbsp;Behind.
            </h2>
            <LazyLoad
              className="min-h-200 relative sm:min-h-400 my-5"
              offset={250}
            >
              <iframe
                allowFullScreen={true}
                className="absolute h-full left-0 top-0 w-full"
                src="https://www.youtube.com/embed/e0e-2-ENOYs"
                title="Keep Moving Forward"
              />
            </LazyLoad>
          </div>
          <div className="text-center">
            <Link
              as={toRelativePageURL("/products", market as string)}
              href={{
                pathname: "/products",
                query: { market },
              }}
            >
              <a className="bg-black border border-black duration-500 ease-in-out font-medium font-serif hover:bg-default hover:text-black inline-block my-5 px-5 py-3 rounded shadow uppercase text-lg text-white transition">
                Shop &amp; Make a Difference
              </a>
            </Link>
          </div>
        </section>
      </>
    );
  }
}
